import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ShoppingCart,
  ArrowRight,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatAmount } from "@/lib/format";

const PLAN_LABELS = {
  esencial: "Esencial",
  avanzado: "Avanzado",
};

export default function CartDrawer() {
  const navigate = useNavigate();
  const {
    plan,
    billing,
    basePrice,
    total,
    annualCharge,
    isOpen,
    setIsOpen,
    setBilling,
    clearCart,
    annualAvailable,
  } = useCart();

  // Bloquear scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  function handleCheckout() {
    setIsOpen(false);
    navigate("/pago");
  }

  const planLabel = plan ? PLAN_LABELS[plan] || plan : null;
  const billingLabel = billing === "annual" ? "Anual" : "Mensual";

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-[#1A1135]/35 backdrop-blur-[2px]"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer panel */}
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 340, damping: 38 }}
            role="dialog"
            aria-modal="true"
            aria-label="Tu pedido"
            className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-sm flex-col bg-white shadow-[-16px_0_48px_-8px_rgba(26,17,53,0.18)]"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-violet-light px-5 py-4 sm:px-6">
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-fuerte">
                  <ShoppingCart className="h-4 w-4 text-white" strokeWidth={2} />
                </div>
                <span className="font-display text-[16px] font-bold text-ink">
                  Tu pedido
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-violet-light bg-white text-ink-2 transition-colors hover:border-violet-fuerte/30 hover:text-violet-fuerte"
                aria-label="Cerrar carrito"
              >
                <X className="h-[18px] w-[18px]" strokeWidth={2} />
              </button>
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-5 sm:p-6">
              {/* Plan selected */}
              {planLabel && (
                <div className="rounded-2xl border border-violet-light bg-[#FAFAFE] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
                        Plan seleccionado
                      </p>
                      <p className="mt-1 font-display text-[20px] font-black text-ink">
                        {planLabel}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearCart}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-red-50 hover:text-red-500"
                      aria-label="Eliminar plan"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                  </div>

                  {/* Billing toggle — solo si anual disponible */}
                  <div className="mt-4">
                    <p className="mb-2 text-[12px] font-semibold text-ink-3">
                      Facturación
                    </p>
                    <div className="inline-flex items-center rounded-full border border-violet-light bg-white p-1">
                      <button
                        type="button"
                        onClick={() => setBilling("monthly")}
                        className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all duration-200 ${
                          billing === "monthly"
                            ? "bg-violet-fuerte text-white shadow-sm"
                            : "text-ink-2 hover:text-ink"
                        }`}
                      >
                        Mensual
                      </button>
                      {annualAvailable && (
                        <button
                          type="button"
                          onClick={() => setBilling("annual")}
                          className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-all duration-200 ${
                            billing === "annual"
                              ? "bg-violet-fuerte text-white shadow-sm"
                              : "text-ink-2 hover:text-ink"
                          }`}
                        >
                          Anual
                          <span
                            className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                              billing === "annual"
                                ? "bg-gold text-ink"
                                : "bg-[#EDF8F3] text-[#2D8A5E]"
                            }`}
                          >
                            −2 meses
                          </span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="font-display text-[28px] font-black text-ink">
                      {formatAmount(basePrice)} €
                    </span>
                    <span className="text-sm text-ink-3">/mes</span>
                  </div>
                  {billing === "annual" && (
                    <p className="mt-0.5 flex items-center gap-1 text-[12px] font-semibold text-[#2D8A5E]">
                      <Sparkles className="h-3 w-3" />
                      Ahorras 2 meses al año
                    </p>
                  )}
                </div>
              )}

              {/* Sin plan seleccionado */}
              {!plan && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-light">
                    <ShoppingCart
                      className="h-6 w-6 text-violet-fuerte"
                      strokeWidth={1.75}
                    />
                  </div>
                  <p className="text-[15px] font-semibold text-ink">
                    Ningún plan elegido
                  </p>
                  <p className="max-w-[200px] text-[13px] leading-relaxed text-ink-3">
                    Selecciona un plan en la sección de precios para continuar.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      document
                        .getElementById("pricing")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="mt-1 text-[14px] font-semibold text-violet-fuerte hover:underline"
                  >
                    Ver planes →
                  </button>
                </div>
              )}
            </div>

            {/* Footer — total + CTA */}
            {plan && (
              <div className="border-t border-violet-light bg-white p-5 sm:p-6">
                {/* Resumen de líneas */}
                <div className="mb-3 space-y-1.5">
                  <div className="flex items-center justify-between text-[13px] text-ink-3">
                    <span>Plan {planLabel}</span>
                    <span>
                      {formatAmount(basePrice)} €/mes
                    </span>
                  </div>
                  {billing === "annual" && (
                    <div className="flex items-center justify-between text-[13px] font-semibold text-[#2D8A5E]">
                      <span>Descuento anual</span>
                      <span>−2 meses gratis</span>
                    </div>
                  )}
                </div>

                {/* Total */}
                {billing === "annual" && annualCharge !== null ? (
                  <div className="rounded-xl border border-violet-light bg-[#FAFAFE] px-4 py-3">
                    <div className="flex items-baseline justify-between">
                      <p className="text-[13px] text-ink-3">
                        Equivale a
                      </p>
                      <p className="font-display text-[18px] font-black text-ink">
                        {formatAmount(total)} €
                        <span className="text-[12px] font-normal text-ink-3">
                          /mes
                        </span>
                      </p>
                    </div>
                    <div className="mt-1.5 flex items-baseline justify-between border-t border-violet-light/60 pt-1.5">
                      <p className="text-[14px] font-bold text-ink">
                        Cargo anual
                      </p>
                      <p className="font-display text-[22px] font-black text-violet-fuerte">
                        {formatAmount(annualCharge)} €
                        <span className="text-[12px] font-normal text-ink-3">
                          /año
                        </span>
                      </p>
                    </div>
                    <p className="mt-1 text-[11px] text-ink-3">
                      Pago único · 2 meses gratis incluidos
                    </p>
                  </div>
                ) : (
                  <div className="flex items-baseline justify-between">
                    <p className="text-[15px] font-bold text-ink">Total</p>
                    <p className="font-display text-[22px] font-black text-ink">
                      {formatAmount(total)} €
                      <span className="text-[13px] font-normal text-ink-3">
                        /mes
                      </span>
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleCheckout}
                  className="mt-4 inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-violet-fuerte text-[16px] font-semibold text-white shadow-[0_4px_20px_-4px_rgba(74,58,138,0.45)] transition-all duration-200 hover:bg-violet-oscuro hover:shadow-[0_6px_28px_-4px_rgba(74,58,138,0.55)] hover:-translate-y-px"
                >
                  Continuar al pago
                  <ArrowRight className="h-5 w-5" strokeWidth={2} />
                </button>

                <p className="mt-3 text-center text-[12px] text-ink-3">
                  Pago seguro con Stripe · Cancela cuando quieras
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
